import { ref, computed, watch, onBeforeUnmount } from 'vue'
import moment from 'moment'
import log from 'loglevel'
import { RestApiClient } from 'ifield-core-components'
import SseClient from '@/lib/SseClient'
import store from '@/store'
import {
  FUNCTION_TYPE,
  ACTIVITY_CODE,
  MAX_SAFE_INTERVAL_DELAY,
} from '@/constants'
import { toLocalTz, toLocalTzAttribute } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { ITEM_TYPE } from '@/composables/member/UseUserStatusAdditionalItems'
import {
  PLACE_RELATIONSHIP,
  STILL_DAYS_OPERATOR,
  SENSOR_VALUE_OPERATOR,
} from '@/composables/member/UseUserStatusFilterCondition'

/**
 * STILL以外のアクティビティコード
 * @type {String}
 */
const ACTIVITIES_NOT_STILL = [...Object.entries(ACTIVITY_CODE)]
  .filter(([key]) => key !== 'STILL')
  .map(([, value]) => value)
  .join(',')

/**
 * 取得ソースの定義
 */
export const SOURCE_TYPE = {
  API: 'api',
  SSE: 'sse',
}

/**
 * 追加項目のAPIパラメータ作成
 * @param {String[]} items 追加項目
 * @returns {Object} パラメータ
 */
const buildItemsParam = (items) => {
  return items.length ? { additional_items: items.join('|') } : {}
}

/**
 * ユーザー種別のAPIパラメータ作成
 * @param {String|null} target 種別指定パラメータ
 * @returns {Object} パラメータ
 */
const buildUserTypeParam = ({ target }) => {
  if (!target) return {}
  return { device_user: target }
}

/**
 * キーワード検索のAPIパラメータ作成
 * @param {String} keyword キーワード
 * @param {String[]} keywordTargets キーワード検索対象項目
 * @returns {Object} パラメータ
 */
const buildKeywordParam = ({ keyword, keywordTargets }) => {
  if (!keyword) return {}

  const targets = keywordTargets.join(',')
  return targets ? { keyword, keyword_target: targets } : { keyword }
}

/**
 * 組織グループのAPIパラメータ作成
 * @param {Number} organizationIds 組織グループID
 * @returns {Object} パラメータ
 */
const buildOrganizationParam = ({ organizationIds }) => {
  if (!organizationIds) return {}

  const ids = organizationIds.join(',')
  return ids ? { organization_id: ids } : {}
}

/**
 * 屋内のAPIパラメータ作成
 * @param {Number[]} indoorBuildingIds ビルID
 * @param {Number} indoorLevelId フロアID
 * @returns {Object} パラメータ
 */
const buildIndoorParam = ({ indoorBuildingIds, indoorLevelId }) => {
  const param = {}
  if (indoorBuildingIds?.length) {
    param.indoor_building_id = indoorBuildingIds.join(',')
  }
  if (indoorLevelId) {
    param.indoor_level_id = indoorLevelId
  }
  return param
}

/**
 * プレイスのAPIパラメータ作成
 * @param {Number} placeId プレイスID
 * @param {String} relationship 対象プレイスとの位置関係性
 * @param {Number} distance プレイスからの距離(m)
 * @returns {Object} パラメータ
 */
const buildPlaceParam = ({ placeId, relationship, distance }) => {
  if (!placeId) {
    return {}
  } else if (relationship === PLACE_RELATIONSHIP.CHECKIN) {
    return { checkin_place_id: placeId }
  } else if (relationship === PLACE_RELATIONSHIP.IS) {
    return { place_id: placeId }
  } else if (relationship === PLACE_RELATIONSHIP.DISTANCE) {
    return { place_distance: `${placeId}:${distance}` }
  } else {
    return {}
  }
}

/**
 * ステイタス種別のAPIパラメータ作成
 * @param {Number} statusTypeId ステイタス種別ID
 * @returns {Object} パラメータ
 */
const buildStatusParam = ({ statusTypeId }) => {
  return statusTypeId ? { status_type_id: statusTypeId } : {}
}

/**
 * 稼働状態のAPIパラメータ作成
 * @param {Boolean} isStill 停止中のみの場合はtrue
 * @returns {Object} パラメータ
 */
const buildActivityParam = ({ isStill }) => {
  if (isStill == null) return {}
  if (isStill === false) return { activity: ACTIVITIES_NOT_STILL }
  return { activity: ACTIVITY_CODE.STILL }
}

/**
 * センサーのAPIパラメータ作成
 * @param {Number} sensorItemId センサー測定項目ID
 * @param {Boolean} canSetValue センサー値指定可能フラグ
 * @param {Number} sensorValue センサー値
 * @param {String} sensorValueOperator センサー値演算子(eq: 等しい, ge: 以上, le: 以内)
 * @returns {Object} パラメータ
 */
const buildSensorParam = ({
  sensorItemId,
  canSetValue,
  sensorValue,
  sensorValueOperator,
}) => {
  if (!sensorItemId) return {}
  if (!canSetValue || sensorValue == null) {
    return { sensor_data: `${sensorItemId}:*` }
  }

  if (sensorValueOperator === SENSOR_VALUE_OPERATOR.EQUAL) {
    return { sensor_data: `${sensorItemId}:${sensorValue}~${sensorValue}` }
  } else if (sensorValueOperator === SENSOR_VALUE_OPERATOR.GREATER) {
    return { sensor_data: `${sensorItemId}:${sensorValue}~` }
  } else {
    return { sensor_data: `${sensorItemId}:~${sensorValue}` }
  }
}

/**
 * ソートのAPIパラメータ作成
 * @param {String} field ソート対象フィールド
 * @param {Boolean} isAsc 昇順にする場合はtrue
 * @returns {Object} パラメータ
 */
const buildSortParam = ({ field, isAsc }) => {
  return field ? { sort_field: field, sort_order: isAsc ? 'ASC' : 'DESC' } : {}
}

/**
 * ローカルタイムゾーンのユーザーデータに変換する
 * @param {Object} d ユーザーデータ(各レコード)
 * @returns {Object} 日時がローカルタイムゾーンになったユーザーデータ
 */
const toLocalTzUserStatus = (d) => ({
  ...d,
  current_status_update_at: toLocalTz(d.current_status_update_at),
  last_positioning_at: toLocalTz(d.last_positioning_at),
  checkin_at: toLocalTz(d.checkin_at),
  checkout_at: toLocalTz(d.checkout_at),
  additional_items: d.additional_items.map((item) => {
    const itemType = item.item_type.split(':')[0]
    switch (itemType) {
      case ITEM_TYPE.ACTIVITY:
        return {
          ...item,
          change_at: toLocalTz(item.change_at),
        }
      case ITEM_TYPE.BATTERY:
        return {
          ...item,
          at: toLocalTz(item.at),
        }
      case ITEM_TYPE.PLACE:
        return {
          ...item,
          checkin_at: toLocalTz(item.checkin_at),
        }
      case ITEM_TYPE.POSITIONING:
        return {
          ...item,
          at: toLocalTz(item.at),
        }
      case ITEM_TYPE.REGISTRATION:
        return {
          ...item,
          at: toLocalTz(item.at),
        }
      case ITEM_TYPE.SENSOR_DATA:
        return {
          ...item,
          at: toLocalTz(item.at),
        }
      case ITEM_TYPE.ATTRIBUTE:
        return toLocalTzAttribute(item, 'value')
      default:
        return item
    }
  }),
})

/**
 * ユーザーが停止期間の点で条件に合う場合はtrue
 * @param {Object} user ユーザーデータ
 * @param {Number} stillDays 停止日数
 * @param {String} stillDaysOperator 停止期間演算子(ge: 以上, le: 以内)
 * @param {Date} baseDate 基準日時
 * @returns {Boolean}
 */
const isUserInStillDays = (
  user,
  { stillDays, stillDaysOperator },
  baseDate
) => {
  const activity = user.additional_items.find(
    (item) => item.item_type === ITEM_TYPE.ACTIVITY
  )
  if (!activity) return false

  const changeAt = moment(activity.change_at)
  const targetAt = moment(baseDate).subtract(+stillDays, 'days')
  if (stillDaysOperator === STILL_DAYS_OPERATOR.GREATER) {
    return targetAt - changeAt >= 0
  } else if (stillDaysOperator === STILL_DAYS_OPERATOR.LESS) {
    return targetAt - changeAt <= 0
  } else {
    return false
  }
}

/**
 * ユーザーが測位状態の点で条件に合う場合はtrue
 * @param {Object} user ユーザーデータ
 * @param {Boolean} isPositioning 測位中のみの場合はtrue
 * @param {Date} baseDate 基準日時
 * @returns {Boolean}
 */
const isUserInPositioning = (user, { isPositioning }, baseDate) => {
  const positioningThreshold = store.state.userList.positioningThreshold
  const targetAt = moment(baseDate).subtract(positioningThreshold, 'minutes')
  const lastPositioningAt = moment(user.last_positioning_at)
  return isPositioning
    ? lastPositioningAt - targetAt >= 0
    : lastPositioningAt - targetAt <= 0
}

/**
 * /user/status の自動取得
 * @param {Ref.<Date>} baseDate 基準日時
 * @param {Ref.<String[]>} items 追加項目
 * @param {Ref.<Object>} condition 絞り込み条件
 * @param {Ref.<Object>} sortRule ソートルール
 */
export function useUserStatusCrawler({ baseDate, items, condition, sortRule }) {
  /**
   * 取得したユーザーデータ
   * @type {Ref.<Object[]>}
   */
  const users = ref([])
  /**
   * 取得ソース
   * @type {Ref.<String>}
   */
  const source = ref(SOURCE_TYPE.API)
  /**
   * 取得した日時
   * @type {Ref.<Date>}
   */
  const fetchedDate = ref(null)
  /**
   * エラー表示済みフラグ
   * @type {Ref.<Boolean>}
   */
  const isErrorShown = ref(false)
  /**
   * インターバルID(非リアクティブ)
   * @type {Number}
   */
  let intervalId

  // エラーハンドリング関数展開
  const { handleApiError } = useHandleApiError()

  /**
   * 高頻度屋内測位の権限がある場合はtrue
   * @type {ComputedRef.<Boolean>}
   */
  const isLocationIndoorHighAvailable = computed(
    () => !!store.getters['functionType'](FUNCTION_TYPE.LOCATION_INDOOR_HIGH)
  )
  /**
   * APIパラメータ
   * @type {ComputedRef.<Object>}
   */
  const apiSearchParam = computed(() => {
    return {
      ...buildItemsParam(items.value),
      ...buildUserTypeParam(condition.value.userType),
      ...buildKeywordParam(condition.value.search),
      ...buildOrganizationParam(condition.value.organization),
      ...buildIndoorParam(condition.value.indoor),
      ...buildPlaceParam(condition.value.place),
      ...buildStatusParam(condition.value.status),
      ...buildActivityParam(condition.value.activity),
      ...buildSensorParam(condition.value.sensor),
      ...buildSortParam(sortRule.value),
    }
  })
  /**
   * APIリクエスト用URL
   * @type {ComputedRef.<URL>}
   */
  const apiUrl = computed(() => {
    const url = RestApiClient.createApiUrl('user/status')
    for (const [key, value] of Object.entries(apiSearchParam.value)) {
      url.searchParams.append(key, value)
    }
    return url
  })
  /**
   * SSE購読用URL
   * @type {ComputedRef.<URL>}
   */
  const sseUrl = computed(() => {
    const url = RestApiClient.createApiUrl('sse')
    for (const [key, value] of Object.entries(apiSearchParam.value)) {
      url.searchParams.append(key, value)
    }
    return url
  })
  /**
   * 自動更新間隔
   * @type {ComputedRef.<Number>}
   */
  const interval = computed(() => {
    if (isLocationIndoorHighAvailable.value) return 1000
    const interval = store.state.userList.updateInterval * 1000
    return interval > MAX_SAFE_INTERVAL_DELAY
      ? MAX_SAFE_INTERVAL_DELAY
      : interval
  })
  /**
   * APIエラーコード
   * @type {ComputedRef.<Number>}
   */
  const errorCode = computed(() => store.state.error.code)
  /**
   * 稼働状態が baseDate でのフィルタリングを必要とする場合はtrue
   * @type {ComputedRef.<Boolean>}
   */
  const needsDateFilterOnActivity = computed(() => {
    const { isStill, canSetDays, stillDays, stillDaysOperator } =
      condition.value.activity
    return isStill && canSetDays && stillDays != null && stillDaysOperator
  })
  /**
   * 測位状態 baseDate でのフィルタリングを必要とする場合はtrue
   * @type {ComputedRef.<Boolean>}
   */
  const needsDateFilterOnPositioning = computed(() => {
    return condition.value.positioning.isPositioning != null
  })
  /**
   * baseDateでフィルタリングしたユーザーデータ
   * @type {ComputedRef.<Object[]>}
   */
  const usersOnDate = computed(() => {
    // 基準日時が渡されていない場合はそのまま
    if (!baseDate || !baseDate.value) return users.value

    if (needsDateFilterOnActivity.value && needsDateFilterOnPositioning.value) {
      return users.value.filter(
        (u) =>
          isUserInStillDays(u, condition.value.activity, baseDate.value) &&
          isUserInPositioning(u, condition.value.positioning, baseDate.value)
      )
    } else if (needsDateFilterOnActivity.value) {
      return users.value.filter((u) =>
        isUserInStillDays(u, condition.value.activity, baseDate.value)
      )
    } else if (needsDateFilterOnPositioning.value) {
      return users.value.filter((u) =>
        isUserInPositioning(u, condition.value.positioning, baseDate.value)
      )
    } else {
      return users.value
    }
  })

  /**
   * 自動更新を開始する
   */
  const startAutoUpdate = () => {
    if (source.value === SOURCE_TYPE.SSE) {
      startListenSse()
    } else {
      startApiPolling()
    }
    log.info('Auto updating user status has started.')
  }

  /**
   * SSE 受信を開始する
   */
  const startListenSse = () => {
    log.info('Auto update by Server Sent Events')
    SseClient.create(
      sseUrl.value,
      'user/status',
      (data) => {
        log.debug('[DEBUG] sse recieved', 'user/status', data)
        users.value = data.map((d) => toLocalTzUserStatus(d))
      },
      (error) => {
        log.warn('Error on listening SSE. start polling', error)
        SseClient.close()
        source.value = SOURCE_TYPE.API
        startApiPolling()
      }
    )
  }

  /**
   * API polling を開始する
   */
  const startApiPolling = () => {
    log.info('Auto update by API polling')
    if (intervalId) {
      log.debug('API polling already exists')
      return
    }
    intervalId = setInterval(fetchAuto, interval.value)
  }

  /**
   * 自動更新を停止する
   */
  const stopAutoUpdate = () => {
    SseClient.close()
    stopApiPolling()
    log.info('Auto updating user status has stopped.')
  }

  /**
   * API polling を停止する
   */
  const stopApiPolling = () => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = undefined
    }
  }

  /**
   * API経由でユーザーデータを取得する
   */
  const fetchFromApi = async () => {
    const json = await RestApiClient.executeGetApi(apiUrl.value)
    users.value = json.data.map((d) => toLocalTzUserStatus(d))
  }

  /**
   * 自動取得する
   */
  const fetchAuto = async () => {
    try {
      await fetchFromApi()
    } catch (error) {
      // URL毎に初回のエラーだけエラー表示する
      if (!isErrorShown.value) {
        handleApiError(error)
        isErrorShown.value = true
      }
    }
  }

  /**
   * 強制的に取得する
   */
  const fetchForce = async () => {
    await fetchFromApi()
    // API polling していたら取得タイミングを仕切り直す
    if (source.value === SOURCE_TYPE.API) {
      stopApiPolling()
      startApiPolling()
    }
  }

  // ユーザー情報更新時に更新日時も更新する
  watch(users, () => {
    fetchedDate.value = new Date()
  })
  // 認証エラー時は自動更新を停止
  watch(
    errorCode,
    (value) => {
      if (value === 401) {
        stopAutoUpdate()
      }
    },
    { immediate: true }
  )
  // APIリクエストURLが変更されたらエラー表示済みフラグを消す
  watch(
    () => apiUrl.value.toString(),
    () => {
      isErrorShown.value = false
    }
  )
  // SSE購読用URLが変更されたらクライアントをつなぎ直す
  watch(
    () => sseUrl.value.toString(),
    () => {
      if (source.value === SOURCE_TYPE.SSE) {
        SseClient.close()
        startListenSse()
      }
    }
  )

  // unmount 時に自動更新は停止
  onBeforeUnmount(() => {
    stopAutoUpdate()
  })

  /**
   * 以下 created 相当処理
   */
  // 高頻度屋内測位権限時はソースをSSEに設定する
  if (isLocationIndoorHighAvailable.value) {
    source.value = SOURCE_TYPE.SSE
  }
  startAutoUpdate()

  return {
    users,
    usersOnDate,
    source,
    fetchedDate,
    fetchForce,
    isErrorShown,
    startAutoUpdate,
    stopAutoUpdate,
  }
}
